import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, DollarSign, Clock, CheckCircle2, Building2,
  TrendingUp, Loader2, Gift, FileText
} from 'lucide-react';
import { toast } from 'sonner';

import { formatDateMonthDay } from '../../utils/timeFormat';
const ClientSavingsTracker = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await api.get('/referrals/bonus-tracker');
      setData(res.data);
    } catch {
      toast.error('Failed to load savings data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  const { summary, transactions } = data || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-savings-tracker">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/client/home')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-lg font-semibold text-gray-900">Savings & Credits</h1>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/client/referrals')}>
            <Gift className="w-4 h-4 mr-1.5" />Referral Program
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="summary-cards">
          <Card>
            <CardContent className="p-4 text-center">
              <DollarSign className="w-6 h-6 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-green-600">${(summary?.total_earned || 0).toFixed(2)}</p>
              <p className="text-xs text-gray-500">Total Saved</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-amber-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-amber-600">${(summary?.total_pending || 0).toFixed(2)}</p>
              <p className="text-xs text-gray-500">Pending Credits</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-emerald-600">${(summary?.total_paid || 0).toFixed(2)}</p>
              <p className="text-xs text-gray-500">Applied to Invoices</p>
            </CardContent>
          </Card>
          <Card className="border-violet-200 bg-violet-50">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 text-violet-600 mx-auto mb-1" />
              <p className="text-2xl font-bold text-violet-700">${(summary?.ytd_earned || 0).toFixed(2)}</p>
              <p className="text-xs text-violet-600 font-medium">YTD Savings</p>
            </CardContent>
          </Card>
        </div>

        {/* How Credits Work */}
        <Card className="border-green-200 bg-green-50/50" data-testid="credit-explanation">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-green-800 mb-1">How Invoice Credits Work</h3>
                <p className="text-sm text-green-700">
                  When you refer a business that completes their first booking, you receive a <strong>$100 credit</strong> that is automatically applied to your next invoice. Credits are non-transferable and have no expiration.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transaction History */}
        <Card data-testid="transaction-history">
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-sm font-semibold">Credit History</CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            {(!transactions || transactions.length === 0) ? (
              <div className="text-center py-8">
                <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500">No credits yet</p>
                <p className="text-xs text-gray-400 mt-1">Refer a business to earn your first $100 credit!</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate('/client/referrals')}>
                  <Gift className="w-4 h-4 mr-1.5" />Refer & Save $100
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {transactions.map(tx => (
                  <div key={tx.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid="transaction-item">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-violet-100">
                        <Building2 className="w-4 h-4 text-violet-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{tx.referred_name}</p>
                        <p className="text-xs text-gray-500">
                          Business referral · {tx.date ? formatDateMonthDay(tx.date) : ''}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-semibold ${tx.status === 'paid' ? 'text-green-600' : 'text-amber-600'}`}>${tx.amount.toFixed(2)}</p>
                      <Badge className={`text-[10px] ${tx.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {tx.status === 'paid' ? 'Applied' : 'Pending'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientSavingsTracker;
